import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import AreaForm from "../../components/AreaForm/AreaForm";
import useAdminAreas from "../../hooks/useAdminAreas";
import useAdminRegions from "../../hooks/useAdminRegions";
import useEditArea from "../../hooks/useEditArea";
import useDeleteArea from "../../hooks/useDeleteArea";
import { adaptAreaPayload } from "../../adapters/adaptAreaPayload";

const BACK_PATH = "/users/locations";

const EMPTY_DEFAULTS: AreaFormValues = {
	name: "",
	region: "",
	areaManager: ""
};

function areaToFormValues(area: LocationArea): AreaFormValues {
	return {
		name: area.name,
		region: area.region ? String(area.region.id) : "",
		areaManager: area.areaManager ? String(area.areaManager.id) : ""
	};
}

const EditArea = () => {
	const navigate = useNavigate();
	const { areaId } = useParams<{ areaId: string }>();
	const id = Number(areaId);
	const isValidId = Number.isInteger(id) && id > 0;

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [defaultValues, setDefaultValues] =
		useState<AreaFormValues>(EMPTY_DEFAULTS);

	const { data: areas = [], isLoading: isLoadingAreas } = useAdminAreas();
	const { data: regions = [] } = useAdminRegions();
	const { mutate: editArea, isLoading: isEditing } = useEditArea();
	const { mutate: deleteArea, isLoading: isDeleting } = useDeleteArea();

	const area = areas.find((a) => a.id === id);

	useEffect(() => {
		if (area) {
			setDefaultValues(areaToFormValues(area));
		}
	}, [area]);

	if (!isValidId) {
		return <Navigate to={BACK_PATH} replace />;
	}

	if (isLoadingAreas) {
		return (
			<Page>
				<div className="flex items-center gap-2 py-12 justify-center">
					<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
					<span className="text-sm text-zinc-600">Loading...</span>
				</div>
			</Page>
		);
	}

	if (!area) {
		return (
			<Page>
				<p className="text-sm text-destructive">Area not found.</p>
			</Page>
		);
	}

	const handleSubmit = (values: AreaFormValues) => {
		const payload = adaptAreaPayload(values);

		editArea(
			{ id, payload },
			{
				onSuccess: () => navigate(BACK_PATH)
			}
		);
	};

	const handleDelete = () => {
		deleteArea(id, {
			onSuccess: () => {
				setShowDeleteModal(false);
				navigate(BACK_PATH);
			}
		});
	};

	return (
		<Page>
			<AreaForm
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				isSubmitting={isEditing}
				regions={regions}
				backPath={BACK_PATH}
				isEdit
				onDelete={() => setShowDeleteModal(true)}
				initialManager={area.areaManager ?? null}
			/>

			<ConfirmationModal
				isOpen={showDeleteModal}
				title="Delete area?"
				description="This action cannot be undone."
				onCancel={() => setShowDeleteModal(false)}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default EditArea;
